/* eslint-disable i18next/no-literal-string */
import React, { Dispatch, SetStateAction } from 'react';
import Downshift from 'downshift';
import styled from 'styled-components';

import { FormTextInput, FormSelectInput } from 'components/form-inputs';
import useMapboxPlacesAutocomplete from 'hooks/use-mapbox-places-autocomplete';
import { StyledTextInput as _StyledTextInput } from 'components/form-inputs/components';
import { statesMap } from 'shared/core/constants/geography';
import { InputRow } from 'src/payments/components';
import { AddressLabelInput, SubmitAddressInput } from './address-form';

export type SetPrefilledValuesType = Dispatch<
  SetStateAction<{ addressLine1: string; addressLine2?: string; city?: string; state?: string; zip?: string }>
>;

type MapBoxDataType = {
  address: string;
  addressLong: string;
  city: string;
  country: string;
  countryLong: string;
  county: string;
  id: string;
  lat: number;
  ln1: string;
  lng: number;
  rootType: string;
  state: string;
  stateLong: string;
  zipcode: string;
};

export type AddAddressProps = {
  setPrefilledValues: SetPrefilledValuesType;
  prefilledValues?: SubmitAddressInput;
  labels: AddressLabelInput;
};

export function AddressAutocomplete({ setPrefilledValues, labels, prefilledValues }: AddAddressProps): JSX.Element {
  const {
    suggestions: { data },
    setValue,
  } = useMapboxPlacesAutocomplete({ requestOptions: { limit: 3 } });

  const handleAddressLine1Change = (event: React.FormEvent<HTMLInputElement>): void => {
    setValue(event.currentTarget.value);

    // Ensure that addressLine1 is set if user skips autocomplete options
    setPrefilledValues({
      ...prefilledValues,
      addressLine1: event.currentTarget.value,
    });
  };

  return (
    <>
      <InputRow>
        <Downshift
          onChange={(item) => {
            setPrefilledValues({
              addressLine1: item?.ln1,
              addressLine2: '',
              city: item?.city,
              state: item?.state,
              zip: item?.zipcode,
            });
          }}
          itemToString={(item) => item?.ln1}
          initialSelectedItem={(item) => item.ln1}
        >
          {({ getInputProps, getItemProps, highlightedIndex, isOpen }) => (
            <div data-private>
              <StyledTextInput
                {...getInputProps({
                  onChange: handleAddressLine1Change,
                  label: labels.addressLine1,
                  'aria-label': 'downshiftAutocomplete',
                  variant: 'filled',
                  name: 'downshiftAutocomplete',
                  defaultValue: prefilledValues?.addressLine1,
                  autoComplete: 'no',
                })}
              />
              {isOpen && (
                <AddressesOverlay>
                  {data.map((item: MapBoxDataType, index: number) => (
                    <Address {...getItemProps({ key: index, index, item })} highLightItem={highlightedIndex === index}>
                      {item.address}
                    </Address>
                  ))}
                </AddressesOverlay>
              )}
            </div>
          )}
        </Downshift>
      </InputRow>

      <HiddenInputRow>
        <FormTextInput name='addressLine1' label={labels.addressLine1} defaultValue='' variant='filled' />
      </HiddenInputRow>

      <InputRow>
        <FormTextInput name='addressLine2' label={labels.addressLine2} defaultValue='' variant='filled' />
      </InputRow>

      <InputRow>
        <FormTextInput name='city' label={labels.city} defaultValue='' variant='filled' />
      </InputRow>

      <InputRow>
        <FormSelectInput name='state' label={labels.state} variant='filled' defaultValue=''>
          {Object.keys(statesMap).map((val: string) => (
            <option key={val} value={val}>
              {val}
            </option>
          ))}
        </FormSelectInput>
        <FormTextInput name='zip' label={labels.zip} defaultValue='' variant='filled' />
      </InputRow>
    </>
  );
}

const HiddenInputRow = styled(InputRow)`
  position: absolute;
  opacity: 0;
  top: 0;
  left: 0;
`;

const StyledTextInput = styled(_StyledTextInput)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const AddressesOverlay = styled.div`
  position: absolute;
  z-index: 500;
  background: ${({ theme }) => theme.colors.white};
  overflow: hidden;
  width: 100%;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
  border-radius: 8px;
`;

const Address = styled.div`
  background: ${({ theme, highLightItem }) => (highLightItem ? theme.colors.primaryGrey : 'transparent')};
  padding: 22px 24px;
  cursor: pointer;
  font-size: 13px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.blueGrey[90]};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  &:last-child {
    border-bottom: 0;
  }
`;
